import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getSubmissions, addSubmission, updateSubmission, deleteSubmission } from '../actions/submissionActions';
import { getSubmissionForms } from '../actions/submissionFormActions';

const TestSubmissionAPI = ({
  submission: { submissions, loading },
  submissionForm: { submissionForms },
  getSubmissions,
  addSubmission,
  updateSubmission,
  deleteSubmission,
  getSubmissionForms
}) => {
  const [formData, setFormData] = useState({
    submissionFormId: '',
    link: '',
    comment: ''
  });
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [editData, setEditData] = useState({
    link: '',
    comment: ''
  });
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    getSubmissionForms();
    getSubmissions();
    
    // Debug authentication
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    setDebugInfo({
      token: token ? `${token.substring(0, 20)}...` : 'No token',
      user: user ? JSON.parse(user) : 'No user',
      tokenLength: token ? token.length : 0,
      submissionFormsCount: submissionForms.length
    });
  }, [getSubmissionForms, getSubmissions, submissionForms.length]);

  const handleSubmit = (e) => {
    e.preventDefault();
    addSubmission(formData);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    updateSubmission(selectedSubmission._id, editData);
  };

  const handleDelete = (id) => {
    deleteSubmission(id);
  };

  const testDirectAPI = async () => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch('/api/submissions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('API Error:', data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Test Submission API</h1>
      
      {/* Debug Info */}
      <div className="bg-yellow-100 border border-yellow-400 p-4 rounded mb-6">
        <h3 className="font-bold mb-2">Debug Info:</h3>
        <pre className="text-sm">{JSON.stringify(debugInfo, null, 2)}</pre>
        <button 
          onClick={testDirectAPI}
          className="mt-2 bg-blue-500 text-white px-4 py-2 rounded"
        >
          Test Direct API Call
        </button>
      </div>

      {/* Submission Forms Debug */}
      <div className="bg-blue-100 border border-blue-400 p-4 rounded mb-6">
        <h3 className="font-bold mb-2">Submission Forms Debug:</h3>
        <p><strong>Total Forms:</strong> {submissionForms.length}</p>
        {submissionForms.length > 0 ? (
          <div className="mt-2">
            <p><strong>Available Forms:</strong></p>
            <ul className="list-disc list-inside text-sm">
              {submissionForms.map(form => (
                <li key={form._id}>
                  {form.title} (Course: {form.courseId?.title || 'No Course'})
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-red-600">No submission forms available for your role/courses</p>
        )}
      </div>

      {/* Create Submission Form */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Create Submission</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Submission Form:</label>
            <select
              value={formData.submissionFormId}
              onChange={(e) => setFormData({...formData, submissionFormId: e.target.value})}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select a form</option>
              {submissionForms.map(form => (
                <option key={form._id} value={form._id}>{form.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Link:</label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({...formData, link: e.target.value})}
              className="w-full p-2 border rounded"
              placeholder="https://example.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Comment:</label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData({...formData, comment: e.target.value})}
              className="w-full p-2 border rounded"
              rows="3"
              placeholder="Your submission comment"
              required
            />
          </div>
          <button
            type="submit"
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Submission'}
          </button>
        </form>
      </div>

      {/* Submissions List */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Submissions ({submissions.length})</h2>
        {loading ? (
          <p>Loading submissions...</p>
        ) : (
          <div className="space-y-4">
            {submissions.map(submission => (
              <div key={submission._id} className="border p-4 rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <p><strong>Form:</strong> {submission.submissionFormId?.title || 'Unknown'}</p>
                    <p><strong>Link:</strong> <a href={submission.link} target="_blank" rel="noopener noreferrer" className="text-blue-500">{submission.link}</a></p>
                    <p><strong>Comment:</strong> {submission.comment}</p>
                    <p><strong>Status:</strong> <span className={`px-2 py-1 rounded text-xs ${
                      submission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>{submission.status}</span></p>
                    <p><strong>Created:</strong> {new Date(submission.createdAt).toLocaleString()}</p>
                  </div>
                  <div className="space-x-2">
                    <button
                      onClick={() => setSelectedSubmission(submission)}
                      className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(submission._id)}
                      className="bg-red-500 text-white px-3 py-1 rounded text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Submission</h3>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Link:</label>
                <input
                  type="url"
                  value={editData.link}
                  onChange={(e) => setEditData({...editData, link: e.target.value})}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Comment:</label>
                <textarea
                  value={editData.comment}
                  onChange={(e) => setEditData({...editData, comment: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows="3"
                  required
                />
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSubmission(null)}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  submission: state.submission,
  submissionForm: state.submissionForm
});

export default connect(mapStateToProps, {
  getSubmissions,
  addSubmission,
  updateSubmission,
  deleteSubmission,
  getSubmissionForms
})(TestSubmissionAPI); 